'use client';

import { useState } from 'react';
import { toast } from 'sonner';
import {
  Check, X, Pencil, Loader2, Play, Undo2, GitCommitHorizontal, TrendingUp, Rocket,
} from 'lucide-react';

// Serialized shapes from /api/improve/recommendations
export interface ImproveExecution {
  id: string;
  recommendation_id: string;
  executed_at: string;
  executor: string;
  status: 'running' | 'done' | 'failed' | 'rolled_back';
  artifact: Record<string, unknown> | null;
  rollback: Record<string, unknown> | null;
}

export interface ImproveRecommendation {
  id: string;
  created_at: string;
  business: string;
  type: string;
  status: 'proposed' | 'approved' | 'rejected' | 'expired';
  title: string;
  evidence: Array<Record<string, unknown>>;
  proposal: Record<string, unknown>;
  projected_impact: Record<string, unknown> | null;
  decided_at: string | null;
  decided_note: string | null;
  execution: ImproveExecution | null;
}

const TYPE_COLORS: Record<string, string> = {
  seo_title: 'bg-cyan-500/15 text-cyan-300',
  seo_meta: 'bg-cyan-500/15 text-cyan-300',
  content_gap: 'bg-purple-500/15 text-purple-300',
  ads_budget: 'bg-amber-500/15 text-amber-300',
  ads_creative: 'bg-amber-500/15 text-amber-300',
  ads_pause: 'bg-red-500/15 text-red-300',
  ab_promote: 'bg-green-500/15 text-green-300',
  reel_idea: 'bg-pink-500/15 text-pink-300',
};

const STATUS_PILL: Record<string, string> = {
  proposed: 'bg-blue-500/15 text-blue-300',
  approved: 'bg-green-500/15 text-green-300',
  rejected: 'bg-red-500/15 text-red-400',
  expired: 'bg-gray-500/15 text-gray-400',
};

function fmtCell(v: unknown): string {
  if (v == null) return '—';
  if (typeof v === 'number') {
    if (v > 0 && v < 1) return `${(v * 100).toFixed(2)}%`; // ctr fractions
    return Number.isInteger(v) ? v.toLocaleString() : v.toFixed(1);
  }
  return String(v);
}

function EvidenceTable({ rows }: { rows: Array<Record<string, unknown>> }) {
  if (!rows.length) return null;
  const cols = Object.keys(rows[0]);
  return (
    <div className="overflow-x-auto rounded-lg border border-white/[0.04]">
      <table className="w-full text-xs">
        <thead>
          <tr className="text-[10px] text-gray-500 uppercase bg-white/[0.02]">
            {cols.map(c => (
              <th key={c} className="text-left font-medium px-3 py-1.5 whitespace-nowrap">{c.replace(/_/g, ' ')}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-white/[0.04]">
              {cols.map(c => (
                <td key={c} className="px-3 py-1.5 text-gray-300 max-w-[280px] truncate" title={String(r[c] ?? '')}>
                  {fmtCell(r[c])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function RecommendationCard({ rec, onChanged }: {
  rec: ImproveRecommendation;
  onChanged: (updated: Partial<ImproveRecommendation> & { id: string }) => void;
}) {
  const [busy, setBusy] = useState<string | null>(null); // 'approve'|'reject'|'edit'|'execute'|'undo'
  const [editing, setEditing] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [editTitle, setEditTitle] = useState(String(rec.proposal.new_title || ''));
  const [rejectNote, setRejectNote] = useState('');

  const p = rec.proposal as { page?: string; current_title?: string | null; new_title?: string; new_meta?: string | null; rationale?: string | null };
  const impact = rec.projected_impact as { metric?: string; current?: number; expected?: number; confidence?: string } | null;
  const exec = rec.execution;
  const executed = exec?.status === 'done';
  const rolledBack = exec?.status === 'rolled_back';

  const decide = async (action: 'approve' | 'reject' | 'edit') => {
    setBusy(action);
    try {
      const body: Record<string, unknown> = { action };
      if (action === 'reject') body.note = rejectNote || undefined;
      if (action === 'edit') body.proposal = { ...rec.proposal, new_title: editTitle.trim() };
      const res = await fetch(`/api/improve/recommendations/${rec.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      onChanged(data.recommendation);
      toast.success(action === 'reject' ? 'Rejected' : action === 'edit' ? 'Edited & approved' : 'Approved');
      setEditing(false); setRejecting(false);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed');
    } finally {
      setBusy(null);
    }
  };

  const execute = async () => {
    setBusy('execute');
    try {
      const res = await fetch(`/api/improve/recommendations/${rec.id}/execute`, { method: 'POST' });
      const data = await res.json();
      if (!res.ok && res.status !== 502) throw new Error(data.error || `HTTP ${res.status}`);
      onChanged({ id: rec.id, execution: data.execution });
      if (data.execution?.status === 'done') toast.success('Executed — commit landed (deploy stays manual)');
      else toast.error(`Execution failed: ${data.execution?.artifact?.error || data.error || 'unknown'}`, { duration: 10000 });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Execute failed');
    } finally {
      setBusy(null);
    }
  };


  const pushDeploy = async () => {
    if (!confirm(`Push ${rec.business} to GitHub and deploy the live site?`)) return;
    setBusy('deploy');
    try {
      const res = await fetch('/api/hub/pushdeploy', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ business: rec.business }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      toast.success(`${data.pushed ? 'Pushed and deploy' : 'Deploy'} dispatched for ${data.domain} — track it on the project's Deploy tab`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Push & Deploy failed', { duration: 10000 });
    } finally {
      setBusy(null);
    }
  };

  const undo = async () => {
    if (!exec) return;
    setBusy('undo');
    try {
      const res = await fetch(`/api/improve/executions/${exec.id}/undo`, { method: 'POST' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      onChanged({ id: rec.id, execution: data.execution });
      toast.success('Reverted');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Undo failed');
    } finally {
      setBusy(null);
    }
  };

  const btnBase = 'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors disabled:opacity-50';

  return (
    <div className="glass-card p-5 space-y-4">
      {/* header row */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`text-[9px] uppercase px-1.5 py-0.5 rounded font-semibold ${TYPE_COLORS[rec.type] || 'bg-gray-500/15 text-gray-400'}`}>
          {rec.type.replace(/_/g, ' ')}
        </span>
        <span className="text-xs text-gray-500">{rec.business}</span>
        <span className={`text-[9px] uppercase px-1.5 py-0.5 rounded font-semibold ${STATUS_PILL[rec.status] || ''}`}>
          {executed ? 'executed' : rolledBack ? 'rolled back' : rec.status}
        </span>
        <span className="ml-auto text-[10px] text-gray-600">{new Date(rec.created_at).toLocaleDateString()}</span>
      </div>

      <h3 className="text-sm font-semibold text-white">{rec.title}</h3>

      {/* evidence */}
      <EvidenceTable rows={rec.evidence} />

      {/* proposal diff */}
      {(p.current_title !== undefined || p.new_title) && (
        <div className="rounded-lg bg-white/[0.02] border border-white/[0.04] p-3 space-y-1.5">
          {p.page && <p className="text-[10px] text-gray-500 truncate" title={p.page}>{p.page}</p>}
          {p.current_title && (
            <p className="text-xs text-gray-500 line-through decoration-red-500/60">{p.current_title}</p>
          )}
          {editing ? (
            <div className="flex items-center gap-2">
              <input
                value={editTitle}
                onChange={e => setEditTitle(e.target.value)}
                className="flex-1 bg-[rgb(var(--background))] border border-cyan-500/40 rounded-lg px-2.5 py-1.5 text-xs text-white focus:outline-none focus:border-cyan-400"
                placeholder="New title"
              />
              <button
                onClick={() => decide('edit')}
                disabled={busy !== null || !editTitle.trim()}
                className={`${btnBase} bg-cyan-500/15 text-cyan-300 hover:bg-cyan-500/25`}
              >
                {busy === 'edit' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                Save & approve
              </button>
              <button onClick={() => setEditing(false)} className={`${btnBase} text-gray-400 hover:text-white`}>Cancel</button>
            </div>
          ) : (
            <p className="text-sm text-green-400 font-medium">{p.new_title}</p>
          )}
          {p.new_meta && <p className="text-[11px] text-gray-400"><span className="text-gray-600">meta:</span> {p.new_meta}</p>}
        </div>
      )}

      {/* projected impact + rationale */}
      {impact && (
        <p className="text-xs text-gray-400 flex items-center gap-1.5">
          <TrendingUp className="w-3.5 h-3.5 text-cyan-400 shrink-0" />
          {(impact.metric || 'metric').toUpperCase()}: {fmtCell(impact.current)} → {fmtCell(impact.expected)} expected
          <span className="text-gray-600">· {impact.confidence || 'unknown'} confidence</span>
        </p>
      )}
      {p.rationale && <p className="text-xs text-gray-500 leading-relaxed italic">{p.rationale}</p>}
      {rec.decided_note && (
        <p className="text-[11px] text-gray-500"><span className="text-gray-600">decision note:</span> {rec.decided_note}</p>
      )}

      {/* actions */}
      <div className="flex items-center gap-2 flex-wrap pt-1">
        {rec.status === 'proposed' && !editing && !rejecting && (
          <>
            <button onClick={() => decide('approve')} disabled={busy !== null}
              className={`${btnBase} bg-green-500/15 text-green-300 hover:bg-green-500/25`}>
              {busy === 'approve' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
              Approve
            </button>
            <button onClick={() => { setEditing(true); setEditTitle(String(rec.proposal.new_title || '')); }} disabled={busy !== null}
              className={`${btnBase} bg-cyan-500/10 text-cyan-300 hover:bg-cyan-500/20`}>
              <Pencil className="w-3.5 h-3.5" /> Edit & approve
            </button>
            <button onClick={() => setRejecting(true)} disabled={busy !== null}
              className={`${btnBase} bg-red-500/10 text-red-400 hover:bg-red-500/20`}>
              <X className="w-3.5 h-3.5" /> Reject
            </button>
          </>
        )}
        {rec.status === 'proposed' && rejecting && (
          <div className="flex items-center gap-2 w-full">
            <input
              value={rejectNote}
              onChange={e => setRejectNote(e.target.value)}
              placeholder="Why? (feeds back to the analyzer)"
              className="flex-1 bg-[rgb(var(--background))] border border-gray-700 rounded-lg px-2.5 py-1.5 text-xs text-white focus:outline-none focus:border-red-500/50"
            />
            <button onClick={() => decide('reject')} disabled={busy !== null}
              className={`${btnBase} bg-red-500/15 text-red-400 hover:bg-red-500/25`}>
              {busy === 'reject' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <X className="w-3.5 h-3.5" />}
              Reject
            </button>
            <button onClick={() => setRejecting(false)} className={`${btnBase} text-gray-400 hover:text-white`}>Cancel</button>
          </div>
        )}

        {rec.status === 'approved' && !executed && !rolledBack && exec?.status !== 'running' && (
          <button onClick={execute} disabled={busy !== null}
            className={`${btnBase} bg-cyan-500/15 text-cyan-300 hover:bg-cyan-500/25`}>
            {busy === 'execute' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
            {busy === 'execute' ? 'Executing… (up to 5 min)' : 'Execute'}
          </button>
        )}
        {exec?.status === 'running' && busy !== 'execute' && (
          <span className="flex items-center gap-1.5 text-xs text-cyan-400">
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Execution in flight…
          </span>
        )}
        {exec?.status === 'failed' && rec.status === 'approved' && (
          <span className="text-[11px] text-red-400 max-w-full truncate" title={String(exec.artifact?.error || '')}>
            Last execution failed: {String(exec.artifact?.error || 'unknown')}
          </span>
        )}

        {executed && exec && (
          <>
            <span className="flex items-center gap-1.5 text-xs text-green-400">
              <GitCommitHorizontal className="w-3.5 h-3.5" />
              <span className="font-mono">{String(exec.artifact?.commit || '')}</span>
              <span className="text-gray-600">· committed, deploy is manual</span>
            </span>
            <button onClick={pushDeploy} disabled={busy !== null}
              className={`${btnBase} bg-cyan-500/10 text-cyan-300 hover:bg-cyan-500/20`}>
              {busy === 'deploy' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Rocket className="w-3.5 h-3.5" />}
              Push & Deploy
            </button>
            <button onClick={undo} disabled={busy !== null}
              className={`${btnBase} bg-amber-500/10 text-amber-300 hover:bg-amber-500/20`}>
              {busy === 'undo' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Undo2 className="w-3.5 h-3.5" />}
              Undo
            </button>
          </>
        )}
        {rolledBack && (
          <span className="text-xs text-amber-300 flex items-center gap-1.5">
            <Undo2 className="w-3.5 h-3.5" /> Reverted
          </span>
        )}
      </div>
    </div>
  );
}
